import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, pluck, switchMap, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { LmoCarriersAndSubcarriers, LmoDriver, LmoDriverService } from '~services/lmo-driver.service';
import { AddSiteRestrictionDialogComponent } from './add-site-restriction-dialog/add-site-restriction-dialog.component';
import { ConfirmActionDialogComponent } from '../../../ui-components/confirm-action-dialog/confirm-action-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SitesService } from '~services/sites.service';
import { Site } from '~models/site';
import { UserService } from '~services/user.service';

@Component({
  selector: 'sa-lmo-driver-site-restrictions-list',
  templateUrl: './lmo-driver-site-restrictions-list.component.html',
  styleUrls: ['./lmo-driver-site-restrictions-list.component.scss'],
})
export class LmoDriverSiteRestrictionsListComponent implements OnInit, OnDestroy {
  public drivers$: Observable<LmoDriver[]>;
  public pageSize$: Observable<number>;
  public page$: Observable<number>;
  public total$: Observable<number>;
  public carriers: Observable<any[]>;

  public filteredCarriers: Observable<any[]>;
  public filteredSubcarriers: Observable<any[]>;
  public filteredSubcarriersOriginal: Observable<any[]>;

  driverSearchControl = new FormControl();
  filteredCarrierControl = new FormControl([]);
  filteredSubcarriersControl = new FormControl([]);

  private subs: Subscription[] = [];
  private activeSites: Site[] = [];
  private siteAccountFracMine: Site[] = [];
  public isNextierAccount: boolean;

  constructor(
    private lmoDriverService: LmoDriverService,
    private siteService: SitesService,
    public userService: UserService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {
    this.drivers$ = this.lmoDriverService.lmoDriversPage$.pipe(pluck('drivers'));
    this.pageSize$ = this.lmoDriverService.lmoDriversPage$.pipe(pluck('pageSize'));
    this.page$ = this.lmoDriverService.lmoDriversPage$.pipe(pluck('pageNumber'));
    this.total$ = this.lmoDriverService.lmoDriversPage$.pipe(pluck('total'));
    this.carriers = this.lmoDriverService.carriersForDriverLMO$;
    this.isNextierAccount = this.userService.isNexTierAccount();
  }

  ngOnInit(): void {
    this.lmoDriverService.getLmoDriverCarriersAndSubcarriers();
    this.setCarrierList();

    this.filteredSubcarriersControl.reset();
    this.filteredSubcarriersControl.disable();

    if (this.lmoDriverService.searchbarValue$$.getValue().searchDriver) {
      this.driverSearchControl.patchValue(this.lmoDriverService.searchbarValue$$.getValue().searchDriver);
    }

    this.siteService.getActiveSites().subscribe((sites) => {
      this.activeSites = sites;
    });

    this.siteService.getSiteAccountFracMine().subscribe((sites) => {
      this.siteAccountFracMine = sites;
    });

    this.subs.push(
      this.filteredCarrierControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((searchTerm) => {
            this.setCarrierList();

            if (searchTerm.length < 1) {
              this.setDriverList();
            }

            return this.filteredCarriers.pipe(
              map((carriers) => {
                const filterValue =
                  typeof searchTerm === 'object' && searchTerm != null
                    ? searchTerm.carrier_name.toLowerCase()
                    : searchTerm?.toLowerCase();
                return filterValue
                  ? carriers.filter((carrier) => carrier.carrier_name.toLowerCase().includes(filterValue))
                  : carriers;
              }),
            );
          }),
        )
        .subscribe((filteredCarriers) => (this.filteredCarriers = of(filteredCarriers))),

      this.filteredCarrierControl.valueChanges.subscribe((selectedCarriers) => {
        if (typeof selectedCarriers === 'object') {
          this.filteredSubcarriersControl.enable();

          this.filteredSubcarriers = this.lmoDriverService.subcarriersForDriverLMO$.pipe(
            map((subcarriers) =>
              subcarriers.filter(
                (subcarrier) =>
                  subcarrier.carrier_id === selectedCarriers.carrier_id && subcarrier.subcarrier_id !== null,
              ),
            ),
          );

          this.filteredSubcarriersOriginal = this.filteredSubcarriers.pipe(
            tap((filteredSubcarriers) => {
              if (!filteredSubcarriers || filteredSubcarriers.length < 1) {
                this.filteredSubcarriersControl.disable();
              }
            }),
          );

          this.lmoDriverService.setLmoDriversSearchCarries(selectedCarriers.carrier_id);
          this.drivers$ = this.lmoDriverService.lmoDriversPage$.pipe(
            map((page) => {
              return page.drivers;
            }),
          );
        } else {
          this.filteredSubcarriersControl.reset();
          this.filteredSubcarriersControl.disable();
        }
      }),

      this.filteredSubcarriersControl.valueChanges.subscribe((selectedSubcarriers) => {
        if (
          typeof selectedSubcarriers === 'object' &&
          !this.filteredSubcarriersControl.disabled &&
          selectedSubcarriers !== null
        ) {
          this.lmoDriverService.setLmoDriversSearchSubcarries(selectedSubcarriers.subcarrier_id);
          this.drivers$ = this.lmoDriverService.lmoDriversPage$.pipe(
            map((page) => {
              return page.drivers;
            }),
          );
        }
      }),

      this.filteredSubcarriersControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((searchTerm) => {
            if (!this.filteredSubcarriersControl.disabled) {
              this.setSubcarrierList();
              return this.filteredSubcarriers.pipe(
                map((subcarriers) => {
                  const filterValue =
                    typeof searchTerm === 'object' && searchTerm != null
                      ? searchTerm.subcarrier_name.toLowerCase()
                      : searchTerm?.toLowerCase();
                  return filterValue
                    ? subcarriers.filter((subcarrier) => subcarrier.subcarrier_name.toLowerCase().includes(filterValue))
                    : subcarriers;
                }),
              );
            }
          }),
        )
        .subscribe((filteredCarriers) => (this.filteredSubcarriers = of(filteredCarriers))),

      this.driverSearchControl.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((search) => {
        this.lmoDriverService.setLmoDriversSearch(search);
        const currentSeachbarValue = this.lmoDriverService.searchbarValue$$.getValue();
        this.lmoDriverService.searchbarValue$$.next({
          ...currentSeachbarValue,
          searchDriver: search,
        });
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  addRestriction(driver: LmoDriver): void {
    const dialogRef = this.matDialog.open(AddSiteRestrictionDialogComponent, {
      width: '500px',
      data: { driver },
    });

    dialogRef.afterClosed().subscribe((result) => {
      return;
    });
  }

  trackByFn(index: number, item: LmoDriver): number {
    return item.id;
  }

  sortChanged(sortParams): void {
    if (sortParams.active && sortParams.direction) {
      this.lmoDriverService.setLmoDriversSort(sortParams.active, sortParams.direction);
    } else {
      this.lmoDriverService.setLmoDriversSort(null, null);
    }
  }

  switchPage(page): void {
    this.lmoDriverService.setLmoDriversPage(page.pageIndex + 1, page.pageSize);
  }

  toggleDetails(driver: LmoDriver) {
    driver.showDetails = !driver.showDetails;
  }

  deleteRestriction(id: number): void {
    const confirmDialogRef = this.matDialog.open(ConfirmActionDialogComponent, {
      data: {
        cancelButtonText: 'Cancel',
        desc: 'Are you sure you want to unban this driver from site? This cannot be undone.',
        submitButtonText: 'Unban',
        title: `Unban Driver from site`,
      },
    });

    confirmDialogRef.afterClosed().subscribe((keepGoing: boolean) => {
      if (!keepGoing) {
        return;
      }

      this.lmoDriverService
        .unbanDriverFromSite(id)
        .then((success: boolean) => {
          if (success) {
            this.snackBar.open('Driver unbanned successfully', null, {
              duration: 5000,
            });
            this.lmoDriverService.getLmoDrivers();
          } else {
            this.snackBar.open('Unban failed!', null, {
              duration: 5000,
            });
          }
        })
        .catch((err) => {
          console.error('Error unbanning driver:', err);
          this.snackBar.open('Error unbanning driver!', null, {
            duration: 5000,
          });
        });
    });
  }

  getSiteRestrictionsCount(driver: LmoDriver): number {
    if (driver.siteRestrictions) {
      return Object.keys(driver.siteRestrictions).length;
    }
    return 0;
  }

  displayCarrierFn(carrier: LmoCarriersAndSubcarriers): string {
    return carrier && carrier.carrier_name ? carrier.carrier_name : '';
  }

  displaySubcarrierFn(subcarrier: LmoCarriersAndSubcarriers): string {
    return subcarrier && subcarrier.subcarrier_name ? subcarrier.subcarrier_name : '';
  }

  setCarrierList() {
    this.filteredCarriers = this.carriers;
  }

  setSubcarrierList() {
    this.filteredSubcarriers = this.filteredSubcarriersOriginal;
  }

  setDriverList() {
    this.lmoDriverService.resetLmoDriversParameters();
    this.drivers$ = this.lmoDriverService.lmoDriversPage$.pipe(pluck('drivers'));
  }

  getRowBackgroundColor(index: number): string {
    return index % 2 === 0 ? 'white' : '#edf6f9';
  }
}
